import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { ActionCreator, compose } from "redux";
import { GetUser } from "../actions/auth/Auth";
import { GetAllLures } from "../actions/lures/Lures";
import { GetPropertyGroup } from "../actions/properties/Properties";
import { ClearSyncQueue, GetAllTrapStatuses, GetTrapInfo, SyncOfflineUpdates, SyncRetry, UpdateTrapCache } from "../actions/traps/Traps";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useSync } from "../hooks/useSync";
import { isUpdateAvailable } from "../serviceWorker";
import SyncConflictModal, { ModalState } from "./common/SyncConflictModal";

interface Props {
  children: React.ReactNode;
  auth: IAuthReducer;
  queue: IQueueReducer;
  properties: IPropertyReducer;
  GetUser: ActionCreator<any>;
  SyncOfflineUpdates: ActionCreator<any>;
  GetAllLures: ActionCreator<any>;
  GetAllTrapStatuses: ActionCreator<any>;
  GetTrapInfo: ActionCreator<any>;
  GetPropertyGroup: ActionCreator<any>;
  UpdateTrapCache: ActionCreator<any>;
  ClearSyncQueue: ActionCreator<any>;
  SyncRetry: ActionCreator<any>;
}

const SyncWrapper = (props: Props) => {
  const { children, auth } = props;

  const isOnline = useOnlineStatus();

  const [syncConflictModalState, setSyncConflictModalState] = useState({
    open: false,
    conflictingRecords: [],
    remainingRecords: { postUpdates: {}, putUpdates: {} },
  } as ModalState);
  const syncOffline = useSync();

  useEffect(() => {
    // Don't sync until online, authenticated, has user data 
    // and no update available.
    if (
      !isOnline ||
      auth.loading ||
      !auth.isAuthenticated ||
      !auth.isPopulated ||
      isUpdateAvailable()
    ) {
      return;
    }
    syncOffline({
      queue: props.queue,
      auth: props.auth,
      properties: props.properties,
      actions: {
        GetAllTrapStatuses: props.GetAllTrapStatuses,
        GetPropertyGroup: props.GetPropertyGroup,
        GetAllLures: props.GetAllLures,
        GetTrapInfo: props.GetTrapInfo,
        UpdateTrapCache: props.UpdateTrapCache,
        SyncOfflineUpdates: props.SyncOfflineUpdates,
        ClearSyncQueue: props.ClearSyncQueue,
        SyncRetry: props.SyncRetry,
      },
      setSyncConflictModalState,
      isOnline,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, props.auth]);

  return (
    <>
      <SyncConflictModal
        modalState={[syncConflictModalState, setSyncConflictModalState]}
      />
      {children}
    </>
  );
};

const mapStateToProps = ({
  auth,
  queue,
  properties,
}: {
  auth: IAuthReducer;
  queue: IQueueReducer;
  properties: IPropertyReducer;
}) => {
  return { auth, queue, properties };
};

export default compose<React.ComponentType<Partial<IRequireAuthProps>>>(
  connect(mapStateToProps, {
    GetUser,
    SyncOfflineUpdates,
    GetAllTrapStatuses,
    GetPropertyGroup,
    GetAllLures,
    GetTrapInfo,
    UpdateTrapCache,
    ClearSyncQueue,
    SyncRetry,
  })
)(SyncWrapper);
